import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAllBillboards, useBillboardCatalog } from '@/features/billboards/api/useBillboardQueries';
import type { BillboardListParams } from '@/features/billboards/api/billboardApi';
import type { BillboardListResponse } from '@/features/billboards/utils/billboardsListUtils';
import {
  matchesBillboardFilters,
  sortBillboards,
  resolveBillboardListPagination,
  resolveListTypeFromTitle,
} from '@/features/billboards/utils/billboardsListUtils';
import { DEFAULT_CITY } from '@/features/landing/components/discoveryTypes';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useAppSelector } from '@/shared/store/hooks';

const DEFAULT_LIMIT = 15;

function paramToArray(value: string | null): string[] {
  if (!value) return [];
  return [value];
}

function hasFilterValue(value?: string | string[]) {
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(value);
}

export function useBillboardsListPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const titleFromState = (location.state as { title?: string } | null)?.title;
  const storeCity = useAppSelector((s) => s.filters.city);
  const storeSearch = useAppSelector((s) => s.filters.search);
  const city = storeCity || DEFAULT_CITY;
  const debouncedSearch = useDebounce(storeSearch, 400);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filters, setFilters] = useState<BillboardListParams>(() => ({
    page: 1,
    limit: DEFAULT_LIMIT,
    city,
    search: storeSearch.trim() || undefined,
    type: resolveListTypeFromTitle(titleFromState),
    locationType: paramToArray(searchParams.get('locationType')),
    venueType: paramToArray(
      searchParams.get('venueType') || searchParams.get('screenType'),
    ),
    VenueSubType: paramToArray(searchParams.get('VenueSubType')),
    screenOrientation: '',
    sortBy: '',
  }));

  useEffect(() => {
    setFilters((prev) => {
      const nextType = resolveListTypeFromTitle(titleFromState);
      if (prev.city === city && prev.type === nextType) return prev;
      return {
        ...prev,
        city,
        page: 1,
        type: nextType,
      };
    });
  }, [city, titleFromState]);

  useEffect(() => {
    setFilters((prev) => {
      const trimmed = debouncedSearch.trim();
      const current = prev.search?.trim() || '';
      if (current === trimmed) return prev;
      const next = { ...prev, page: 1 };
      if (trimmed) next.search = trimmed;
      else delete next.search;
      return next;
    });
  }, [debouncedSearch]);

  const hasOrientationFilter = Boolean(filters.screenOrientation);
  const hasSortFilter = Boolean(filters.sortBy);
  const hasLocationTypeFilter = hasFilterValue(filters.locationType);
  const hasVenueTypeFilter = hasFilterValue(filters.venueType);
  const hasSubTypeFilter = hasFilterValue(filters.VenueSubType);
  const hasAreaFilter = Boolean(filters.area?.trim());
  const hasLocalityFilter = Boolean(filters.locality?.trim());
  const hasCoordFilter = Boolean(
    filters.lat != null &&
    filters.lng != null &&
    Number.isFinite(filters.lat) &&
    Number.isFinite(filters.lng)
  );

  const hasClientFiltering =
    hasOrientationFilter ||
    hasSortFilter ||
    hasLocationTypeFilter ||
    hasVenueTypeFilter ||
    hasSubTypeFilter ||
    hasAreaFilter ||
    hasLocalityFilter ||
    hasCoordFilter;

  const page = filters.page ?? 1;
  const limit = filters.limit ?? DEFAULT_LIMIT;

  const query = useAllBillboards(filters, !hasClientFiltering);
  const catalogQuery = useBillboardCatalog(filters, hasClientFiltering);

  const payload = query.data as BillboardListResponse | undefined;
  const pagedBillboards = payload?.data?.billboards ?? [];
  const pagedPagination = resolveBillboardListPagination(
    payload?.data?.pagination ?? payload?.pagination,
    pagedBillboards.length,
    { page, limit },
  );

  const clientFilteredBillboards = useMemo(() => {
    if (!hasClientFiltering) return [];
    const catalogItems = catalogQuery.data?.billboards ?? [];
    const matched = catalogItems.filter((item) =>
      matchesBillboardFilters(item, filters),
    );
    return sortBillboards(matched, filters.sortBy);
  }, [catalogQuery.data?.billboards, hasClientFiltering, filters]);

  const billboards = hasClientFiltering
    ? clientFilteredBillboards.slice((page - 1) * limit, page * limit)
    : pagedBillboards;

  const pagination = hasClientFiltering
    ? {
        page,
        limit,
        total: clientFilteredBillboards.length,
        pages: Math.max(
          1,
          Math.ceil(clientFilteredBillboards.length / limit) || 1,
        ),
      }
    : pagedPagination;

  const listQuery = hasClientFiltering ? catalogQuery : query;

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (hasFilterValue(filters.locationType)) count += 1;
    if (hasFilterValue(filters.venueType)) count += 1;
    if (hasFilterValue(filters.VenueSubType)) count += 1;
    if (filters.screenOrientation) count += 1;
    if (filters.sortBy) count += 1;
    if (filters.lat && filters.lng) count += 1;
    if (filters.area?.trim()) count += 1;
    if (filters.locality?.trim() && (!filters.lat || !filters.lng)) count += 1;
    return count;
  }, [filters]);

  useEffect(() => {
    if (!listQuery.isPending && pagination.total > 0 && page > pagination.pages) {
      setFilters((prev) => ({ ...prev, page: pagination.pages }));
    }
  }, [listQuery.isPending, pagination.total, pagination.pages, page]);

  const setPage = (nextPage: number) => {
    const maxPages = pagination.pages || 1;
    const clamped = Math.max(1, Math.min(nextPage, maxPages));
    setFilters((prev) => ({ ...prev, page: clamped }));
  };

  const applyFilters = (next: Partial<BillboardListParams>) => {
    setFilters((prev) => {
      const merged = { ...prev, ...next, page: 1 };
      if (!merged.lat || !merged.lng) {
        delete merged.lat;
        delete merged.lng;
        delete merged.radius;
        delete merged.areaFilter;
      }
      if (!merged.locality?.trim()) delete merged.locality;
      else merged.locality = merged.locality.trim();
      if (!merged.area?.trim()) delete merged.area;
      else merged.area = merged.area.trim();
      return merged;
    });
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setFilters((prev) => {
      const next = { ...prev };
      next.page = 1;
      next.locationType = [];
      next.venueType = [];
      next.VenueSubType = [];
      next.screenOrientation = '';
      next.sortBy = '';
      next.locality = '';
      delete next.lat;
      delete next.lng;
      delete next.radius;
      delete next.areaFilter;
      delete next.area;
      return next;
    });
  };

  return {
    city,
    titleFromState,
    filters,
    setFilters,
    filtersOpen,
    setFiltersOpen,
    billboards,
    pagination,
    isPending: listQuery.isPending,
    isError: listQuery.isError,
    errorMessage:
      listQuery.error instanceof Error
        ? listQuery.error.message
        : listQuery.isError
          ? 'Failed to load screens'
          : undefined,
    activeFilterCount,
    setPage,
    applyFilters,
    clearFilters,
  };
}
